import { ABP, RoutesService, TreeNode } from '@abp/ng.core';
import { inject, Injectable } from '@angular/core';

/** Paths that must never be offered as a landing destination. */
const NON_LANDING_PATHS = ['/', '/home', '/access-denied'];

interface LandingCandidate {
  path: string;
  requiredPolicy?: string;
}

/**
 * Answers "which screens can the current user actually reach?" from the ABP menu.
 *
 * `RoutesService.visible` is already the right source: it is permission-filtered
 * (`hide = invisible || !isGranted(requiredPolicy)`), `order`-sorted, and it re-emits once the
 * granted policies arrive after login.
 */
@Injectable({ providedIn: 'root' })
export class AccessibleRoutesService {
  private readonly routesService = inject(RoutesService);

  /**
   * Path of the screen a user should land on when the home dashboard is not available to them,
   * or `undefined` when the user can reach no screen at all.
   *
   * A route that carries a `requiredPolicy` is preferred over one that does not: an ungated route
   * is visible to everybody, so it says nothing about what this user was actually granted and its
   * data calls would very likely be refused by the backend.
   */
  public findLandingPath(): string | undefined {
    const candidates = this.collectCandidates(this.routesService.visible);
    const explicitlyGranted = candidates.find(candidate => !!candidate.requiredPolicy);

    return (explicitlyGranted ?? candidates[0])?.path;
  }

  /** Whether the user can reach at least one screen besides the home dashboard. */
  public hasAnyAccessibleScreen(): boolean {
    return this.findLandingPath() !== undefined;
  }

  /**
   * Pre-order depth-first walk of the visible tree. Menu container nodes carry no `path` and only
   * contribute their children; a container whose children were all permission-filtered away
   * therefore yields nothing.
   */
  private collectCandidates(nodes: TreeNode<ABP.Route>[]): LandingCandidate[] {
    return nodes.reduce<LandingCandidate[]>((candidates, node) => {
      const path = this.toAbsolutePath(node.path);

      if (path && !NON_LANDING_PATHS.includes(path)) {
        candidates.push({ path, requiredPolicy: node.requiredPolicy });
      }

      return [...candidates, ...this.collectCandidates(node.children ?? [])];
    }, []);
  }

  /** Route providers register paths both with and without a leading slash. */
  private toAbsolutePath(path: string | undefined): string | undefined {
    if (!path) {
      return undefined;
    }

    return path.startsWith('/') ? path : `/${path}`;
  }
}
